// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const Header = () => {
  const { itemCount } = useCart();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/categories', label: 'Categories' },
    { path: '/about', label: 'About' },
  ];

  const contactInfo = {
    phone: '(202) 413-7664',
    text: '(202) 413-7664',
    address: 'Washington, DC',
    hours: 'Mon-Sun: 9AM - 10PM'
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('header')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (showContactModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showContactModal]);

  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    setShowContactModal(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCallNow = () => {
    window.location.href = `tel:${contactInfo.phone.replace(/\D/g, '')}`;
  };

  const handleTextNow = () => {
    window.location.href = `sms:${contactInfo.text.replace(/\D/g, '')}`;
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          {/* Logo */}
          <Link to="/" onClick={() => handleNavClick('/')} className="logo">
            <span className="logo-icon">🌿</span>
            <div className="logo-text">
              <span className="logo-names">DESIGNERS DC</span>
              <span className="logo-tagline">Dispensary & Delivery</span>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="actions">
            <button onClick={() => setShowContactModal(true)} className="action-btn contact-btn">
              <span className="action-icon">📞</span>
              <span className="contact-number">{contactInfo.phone}</span>
            </button>

            <Link to="/cart" onClick={() => handleNavClick('/cart')} className="action-btn cart-btn">
              <span className="action-icon">🛒</span>
              {itemCount > 0 && <span className="cart-count">{itemCount}</span>}
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`menu-btn ${isMenuOpen ? 'open' : ''}`}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-menu-content">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`mobile-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mobile-contact">
                <div className="mobile-contact-title">Contact</div>
                <button onClick={handleCallNow} className="mobile-contact-item">
                  <span>📞</span>
                  <span>{contactInfo.phone}</span>
                  <span className="mobile-action">Call</span>
                </button>
                <button onClick={handleTextNow} className="mobile-contact-item">
                  <span>💬</span>
                  <span>{contactInfo.text}</span>
                  <span className="mobile-action">Text</span>
                </button>
                <div className="mobile-contact-item">
                  <span>📍</span>
                  <div className="mobile-address">
                    <div>{contactInfo.address}</div>
                    <div className="mobile-hours">{contactInfo.hours}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Contact Us</h2>
              <button className="modal-close" onClick={() => setShowContactModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <button onClick={handleCallNow} className="modal-contact">
                <div className="modal-icon call">📞</div>
                <div>
                  <div className="modal-label">Call us</div>
                  <div className="modal-value">{contactInfo.phone}</div>
                </div>
                <span className="modal-arrow">→</span>
              </button>
              <button onClick={handleTextNow} className="modal-contact">
                <div className="modal-icon text">💬</div>
                <div>
                  <div className="modal-label">Text us</div>
                  <div className="modal-value">{contactInfo.text}</div>
                </div>
                <span className="modal-arrow">→</span>
              </button>
              <div className="modal-contact address">
                <div className="modal-icon address">📍</div>
                <div>
                  <div className="modal-label">Visit us</div>
                  <div className="modal-value">{contactInfo.address}</div>
                  <div className="modal-hours">{contactInfo.hours}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .header {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: white;
          border-bottom: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .header.scrolled {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .header-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0.75rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Logo */
        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
        }

        .logo-icon {
          font-size: 2rem;
          background: #059669;
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
          color: white;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
        }

        .logo-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          line-height: 1.2;
          letter-spacing: -0.01em;
        }

        .logo-tagline {
          font-size: 0.7rem;
          color: #6b7280;
        }

        @media (max-width: 480px) {
          .logo-tagline {
            display: none;
          }
          .logo-name {
            font-size: 1rem;
          }
          .logo-icon {
            width: 2rem;
            height: 2rem;
            font-size: 1.5rem;
          }
        }

        /* Navigation */
        .nav {
          display: flex;
          gap: 2rem;
        }

        @media (max-width: 768px) {
          .nav {
            display: none;
          }
        }

        .nav-link {
          color: #374151;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.95rem;
          transition: color 0.2s ease;
          padding: 0.25rem 0;
        }

        .nav-link:hover {
          color: #059669;
        }

        .nav-link.active {
          color: #059669;
          font-weight: 600;
          border-bottom: 2px solid #059669;
        }

        /* Actions */
        .actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .action-btn {
          position: relative;
          background: none;
          border: none;
          padding: 0.5rem;
          cursor: pointer;
          color: #374151;
          font-size: 1.25rem;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .action-btn:hover {
          background: #f3f4f6;
        }

        .contact-btn {
          background: #f3f4f6;
          padding: 0.5rem 1rem;
        }

        .contact-number {
          font-size: 0.9rem;
          font-weight: 500;
          color: #111827;
        }

        @media (max-width: 1024px) {
          .contact-number {
            display: none;
          }
          .contact-btn {
            background: none;
            padding: 0.5rem;
          }
        }

        .cart-btn {
          position: relative;
        }

        .cart-count {
          position: absolute;
          top: -2px;
          right: -2px;
          background: #059669;
          color: white;
          font-size: 0.7rem;
          font-weight: 600;
          min-width: 1.25rem;
          height: 1.25rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Menu Button */
        .menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-around;
          width: 2rem;
          height: 2rem;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
        }

        @media (max-width: 768px) {
          .menu-btn {
            display: flex;
          }
        }

        .menu-btn span {
          width: 100%;
          height: 2px;
          background: #374151;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .menu-btn.open span:first-child {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .menu-btn.open span:nth-child(2) {
          opacity: 0;
        }

        .menu-btn.open span:last-child {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* Mobile Menu */
        .mobile-menu {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border-top: 1px solid #e5e7eb;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .mobile-menu-content {
          padding: 1rem;
        }

        .mobile-link {
          display: block;
          padding: 1rem;
          color: #374151;
          text-decoration: none;
          font-weight: 500;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }

        .mobile-link.active {
          color: #059669;
          background: #f0fdf4;
        }

        .mobile-link:hover {
          background: #f9fafb;
        }

        .mobile-contact {
          margin-top: 1.5rem;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 0.75rem;
          border: 1px solid #e5e7eb;
        }

        .mobile-contact-title {
          font-weight: 600;
          color: #059669;
          margin-bottom: 1rem;
          font-size: 1rem;
        }

        .mobile-contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 0.75rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mobile-contact-item:hover {
          border-color: #059669;
          background: white;
        }

        .mobile-contact-item span:first-child {
          font-size: 1.25rem;
        }

        .mobile-action {
          color: #059669;
          font-weight: 500;
          margin-left: auto;
        }

        .mobile-address {
          flex: 1;
          text-align: left;
        }

        .mobile-hours {
          font-size: 0.8rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
          padding: 1rem;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal {
          background: white;
          border-radius: 1rem;
          max-width: 450px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: scaleIn 0.3s ease;
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .modal-contact {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          text-align: left;
        }

        .modal-contact:hover:not(.address) {
          background: #f0fdf4;
          border-color: #059669;
        }

        .modal-contact.address {
          cursor: default;
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .modal-icon.call {
          background: #059669;
        }

        .modal-icon.text {
          background: #7c3aed;
        }

        .modal-icon.address {
          background: #6b7280;
        }

        .modal-label {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        .modal-value {
          font-weight: 600;
          color: #111827;
        }

        .modal-hours {
          font-size: 0.8rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        .modal-arrow {
          margin-left: auto;
          color: #059669;
          font-size: 1.25rem;
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .header {
            background: #1f2937;
            border-color: #374151;
          }
          .logo-name {
            color: #f9fafb;
          }
          .logo-tagline {
            color: #9ca3af;
          }
          .nav-link {
            color: #e5e7eb;
          }
          .nav-link:hover,
          .nav-link.active {
            color: #10b981;
          }
          .action-btn {
            color: #e5e7eb;
          }
          .action-btn:hover {
            background: #374151;
          }
          .contact-btn {
            background: #374151;
          }
          .contact-number {
            color: #e5e7eb;
          }
          .menu-btn span {
            background: #e5e7eb;
          }
          .mobile-menu {
            background: #1f2937;
            border-color: #374151;
          }
          .mobile-link {
            color: #e5e7eb;
          }
          .mobile-link.active {
            background: #374151;
          }
          .mobile-link:hover {
            background: #374151;
          }
          .mobile-contact {
            background: #111827;
            border-color: #374151;
          }
          .mobile-contact-item {
            background: #1f2937;
            border-color: #374151;
            color: #e5e7eb;
          }
          .modal {
            background: #1f2937;
          }
          .modal-header {
            border-color: #374151;
          }
          .modal-title {
            color: #f9fafb;
          }
          .modal-close {
            color: #9ca3af;
          }
          .modal-close:hover {
            background: #374151;
            color: #f9fafb;
          }
          .modal-contact {
            background: #111827;
            border-color: #374151;
          }
          .modal-contact:hover:not(.address) {
            background: #374151;
          }
          .modal-label {
            color: #9ca3af;
          }
          .modal-value {
            color: #f9fafb;
          }
        }

        /* Touch devices */
        @media (hover: none) {
          .action-btn:hover,
          .mobile-contact-item:hover,
          .modal-contact:hover {
            transform: none;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;